import { Badge, Button, Color, Radius, Spacing, Text } from 'zens-storybook'
import React from 'react'
import { View, StyleSheet, ViewProps } from 'react-native'
import { Trans, useTranslation } from 'react-i18next'
import { Floorplan } from 'zens-store'

import { Link } from 'zens-router'

import { FloorplanAsset } from '../floorplan'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  top: {
    flex: 1,
    minHeight: 240 + Spacing.S * 2,
    padding: Spacing.S,
    borderBottomWidth: 1,
    borderBottomColor: Color.calmGrey,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    paddingTop: Spacing.S,
    paddingHorizontal: Spacing.M,
  },
  empty: { color: Color.metalGrey },
  number: {
    color: Color.metalGrey,
  },
  edit: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginVertical: Spacing.S,
    marginHorizontal: Spacing.S,
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  map: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
  },
  draft: {
    height: 40,
    justifyContent: 'flex-end',
  },
  badge: {
    paddingHorizontal: Spacing.S,
  },
  skeletonMap: {
    height: 240,
    width: '100%',
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
  },
})

interface Props extends ViewProps {
  floorplan: Floorplan
  companyId: number
  locationId: number
  buildingId: number
}
const FloorCard = (props: Props) => {
  const { floorplan, companyId, locationId, buildingId, style, ...otherProps } = props
  const { name, number, background, isPublished } = floorplan
  const { t } = useTranslation()

  const renderFloorPlan = () => {
    if (!background)
      return (
        <Text style={styles.empty}>
          <Trans>No floorplan uploaded yet</Trans>
        </Text>
      )
    return (
      <View style={styles.map}>
        <FloorplanAsset uri={background} width="100%" height="auto" />
        <View style={styles.draft}>{!isPublished && <Badge title={t('Draft')} light style={styles.badge} />}</View>
      </View>
    )
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.top}>{renderFloorPlan()}</View>
      <View style={styles.content}>
        <Text level={4} style={styles.number}>
          {number}
        </Text>
        <Text level={4}>{name}</Text>
      </View>
      <View style={styles.edit}>
        <Link
          href={`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${floorplan.id}`}
          asChild
        >
          <Button title={t('View')} light testID="FloorCard.View" style={styles.button} disabled={!background} />
        </Link>
        <Link
          href={`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${floorplan.id}/edit`}
          asChild
        >
          <Button title={t('Edit')} testID="FloorCard.Edit" style={styles.button} />
        </Link>
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
FloorCard.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.top}>
        <View style={styles.skeletonMap} />
      </View>
      <View style={styles.content}>
        <Text.Skeleton level={4} />
        <Text.Skeleton level={4} />
      </View>
      <View style={styles.edit}>
        <Button.Skeleton />
      </View>
    </View>
  )
}

export default FloorCard
